import { captureException } from '@sentry/nextjs';
import { arSA } from 'date-fns/locale/ar-SA';
import { enGB } from 'date-fns/locale/en-GB';
// Don't try to upgrade to date-fns/tz yet!
// It relies heavily on https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
// Which is only supported from >= iOS 15.4.
// We support iOS >= 15.1 (following react-native support roadmap https://github.com/facebook/react-native?tab=readme-ov-file#-requirements)
// When react-native discontinues support for iOS 15.1 and our App is on this version, we can upgrade to date-fns/tz.
import { format } from 'date-fns-tz';

import { SiteLocale } from 'constants/i18n.constants';

export const DateFormat = {
  ShortDayDate: {
    [SiteLocale.EN]: 'E d MMM',
    [SiteLocale.AR]: 'EEEE d MMMM',
  },
  ShortWithoutDayDate: {
    [SiteLocale.EN]: 'd MMM',
    [SiteLocale.AR]: 'd MMMM',
  },
  ShortDayDateTime: {
    [SiteLocale.EN]: 'E d MMM',
    [SiteLocale.AR]: 'EEEE d MMMM',
  },
  LongDayDate: {
    [SiteLocale.EN]: 'E d MMM yyyy',
    [SiteLocale.AR]: 'EEEE d MMMM yyyy',
  },
  Month: {
    [SiteLocale.EN]: 'MMM',
    [SiteLocale.AR]: 'MMMM',
  },
  WeekdayDate: {
    [SiteLocale.EN]: 'EEEE, MMM dd',
    [SiteLocale.AR]: 'EEEE، d MMMM',
  },
  Weekday: 'EEEE',
  /** Calendar weekday headings. */
  WeekdayShort: 'EEE',
  WeekdayDay: 'EEEE d',
  DayOfMonth: 'd',
  ShortMonthYear: {
    [SiteLocale.EN]: 'MMM yyyy',
    [SiteLocale.AR]: 'MMMM yyyy',
  },
  MonthYear: 'MMMM yyyy',
  /** Calendar month heading. The comma is per design. */
  MonthYearLong: {
    [SiteLocale.EN]: 'MMMM, yyyy',
    [SiteLocale.AR]: 'MMMM، yyyy',
  },
  DateTime: 'dd/MM/yyyy hh:mma',
  Date: 'dd/MM/yyyy',
  ShortTime: 'h:mma',
  Time: 'h:mm a',
  /** Groups by calendar day; never shown to a reader. */
  DayKey: 'yyyy-MM-dd',
  /** Event-day sheet heading, e.g. "July 10, 2026". */
  FullMonthDate: {
    [SiteLocale.EN]: 'MMMM d, yyyy',
    [SiteLocale.AR]: 'd MMMM yyyy',
  },
  /** Event-day row date, e.g. "July 10". */
  MonthDay: {
    [SiteLocale.EN]: 'MMMM d',
    [SiteLocale.AR]: 'd MMMM',
  },
} as const;

const dateFnsLocales = {
  [SiteLocale.EN]: enGB,
  [SiteLocale.AR]: arSA,
};

export const i18nFormat = (
  date: Date | number,
  formatString: string,
  locale: SiteLocale = SiteLocale.EN,
  options: Omit<Parameters<typeof format>[2], 'locale'> = {},
): string => {
  try {
    return format(date, formatString, {
      ...options,
      locale: dateFnsLocales[locale],
    });
  } catch (error) {
    captureException(error, {
      tags: {
        service: 'date-fns-tz',
        func: 'format',
      },
      extra: {
        date: date?.toString(),
        formatString,
        locale,
        options,
      },
    });
    return '';
  }
};

export const getCurrentISOString = () => new Date().toISOString();
